/** biome-ignore-all lint/correctness/useExhaustiveDependencies: <explanation> */
import { useQueryClient } from "@tanstack/react-query";
import Plivo from "plivo-browser-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { makePstnCall } from "./api";
import type {
	CallStatus,
	PlivoConfig,
	UsePlivoParams,
	UsePlivoReturn,
} from "./types";

const PLIVO_CONFIG: PlivoConfig = {
	debug: "ERROR",
	permOnClick: true,
	enableTracking: true,
	closeProtection: false,
	maxAverageBitrate: 48000,
	enableNoiseReduction: true,
};

export function usePlivo({
	agent,
	numberToCall,
	countryCode,
	fromNumber,
	playDisclaimer,
	plivoPassword,
}: UsePlivoParams): UsePlivoReturn {
	const queryClient = useQueryClient();
	// biome-ignore lint/suspicious/noExplicitAny: plivo-browser-sdk types are incomplete
	const plivoClient = useRef<any>(null);
	const callTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const [callStatus, setCallStatus] = useState<CallStatus>("");
	const [isMuted, setIsMuted] = useState(false);
	const [isCalling, setIsCalling] = useState(false);
	const [callDuration, setCallDuration] = useState(0);
	const [isLoading, setIsLoading] = useState(true);
	const [isConnected, setIsConnected] = useState(false);
	const [isNoiseReductionOn, setIsNoiseReductionOn] = useState(false);
	const [inputVolume, setInputVolume] = useState(0);
	const [outputVolume, setOutputVolume] = useState(0);

	const isPstn = !!agent.data.enable_pstn_calling;
	const agentCountry =
		agent.user_details.phone_details?.country?.toUpperCase() ?? "IN";

	const plivoUsername = useMemo(() => {
		if (agentCountry !== "IN") return agent.data.plivo_username || "";
		if (
			countryCode === "IN" ||
			numberToCall?.startsWith("91") ||
			numberToCall?.startsWith("+91")
		) {
			return agent.data.plivo_username_ind || "";
		}
		return agent.data.plivo_username || "";
	}, [numberToCall, countryCode, agentCountry, agent.data]);

	const startTimer = () => {
		stopTimer();
		setCallDuration(0);
		callTimerRef.current = setInterval(
			() => setCallDuration((d) => d + 1),
			1000,
		);
	};

	const stopTimer = () => {
		if (callTimerRef.current) {
			clearInterval(callTimerRef.current);
			callTimerRef.current = null;
		}
	};

	const clearCallLogs = () => localStorage.removeItem("PlivoLogStorage");

	const onCallEnd = () => {
		setIsCalling(false);
		setCallStatus("");
		setInputVolume(0);
		setOutputVolume(0);
		stopTimer();
		clearCallLogs();
		queryClient.invalidateQueries({ queryKey: ["call-logs"] });
		queryClient.invalidateQueries({ queryKey: ["ai-calls"] });
	};

	useEffect(() => {
		clearCallLogs();

		if (isPstn) {
			setIsLoading(false);
			return;
		}

		if (!plivoClient.current) {
			plivoClient.current = new Plivo(PLIVO_CONFIG);
		}

		const client = plivoClient.current?.client;
		if (!client) return;

		if (!plivoPassword) {
			toast.error("Plivo Credentials Missing", {
				description: "Please contact support to configure Plivo credentials",
			});
			return;
		}

		navigator.mediaDevices.getUserMedia({ audio: true }).catch(() => {
			toast.error("Microphone Permission Denied", {
				description: "Please allow microphone access to make calls",
			});
		});

		client.login(plivoUsername, plivoPassword);

		client.on("onLogin", () => {
			setIsLoading(false);
			setIsConnected(true);
			toast.success("Plivo connected");
		});

		client.on("onLoginFailed", () => {
			setIsLoading(true);
			setIsConnected(false);
			toast.error("Login Failed", {
				description: "Please contact support to configure Plivo credentials",
			});
		});

		client.on("onWebrtcNotSupported", () => setIsLoading(true));

		// Connection status monitoring
		// biome-ignore lint/suspicious/noExplicitAny: plivo-browser-sdk types incomplete
		client.on("onConnectionChange", (event: any) => {
			const connected = event?.state === "connected";
			setIsConnected(connected);
			if (!connected) {
				toast.warning("Connection lost", {
					description: "Reconnecting to Plivo…",
				});
			}
		});

		// Media quality metrics (speaking-while-muted detection)
		// biome-ignore lint/suspicious/noExplicitAny: plivo-browser-sdk types incomplete
		client.on("mediaMetrics", (event: any) => {
			if (event?.type === "speaking_on_mute" && event?.active) {
				toast.warning("You're speaking while muted", {
					id: "speaking-on-mute",
					description: "Unmute your microphone to be heard",
				});
			}
		});

		// Real-time volume levels
		// biome-ignore lint/suspicious/noExplicitAny: plivo-browser-sdk types incomplete
		client.on("volume", (stats: any) => {
			setInputVolume(stats?.inputVolume ?? 0);
			setOutputVolume(stats?.outputVolume ?? 0);
		});

		client.on("onCalling", () => {
			setCallStatus("Calling");
			setIsCalling(true);
			setCallDuration(0);
			toast.info("Calling...");
		});

		client.on("onCallRemoteRinging", () => {
			setCallStatus("Ringing");
			setIsCalling(true);
		});

		client.on("onCallAnswered", () => {
			setCallStatus("Answered");
			setIsCalling(true);
			startTimer();
			toast.success("Call Answered");
		});

		client.on("onCallTerminated", () => {
			toast.info("Call Ended");
			onCallEnd();
		});

		client.on("onCallFailed", (cause: string) => {
			toast.error("Call Failed", {
				description:
					cause?.toLowerCase() === "busy"
						? "Unanswered call limit reached!"
						: cause,
			});
			onCallEnd();
		});

		return () => {
			stopTimer();
			client.removeAllListeners();
			client.logout();
			clearCallLogs();
		};
	}, [plivoUsername]);

	const makeCall = () => {
		if (!numberToCall) return;

		if (isPstn) {
			makePstnCall({
				to: numberToCall,
				from: fromNumber || undefined,
				isRecordingDisclaimer: true,
			})
				.then(() =>
					toast.success("Initiated Call", {
						description: "Please answer the call on your phone",
					}),
				)
				.catch(() => toast.error("Failed to initiate call"));
			return;
		}

		const client = plivoClient.current?.client;
		if (!client?.isRegistered() || !client?.isConnected()) {
			toast.error("Client Not Connected", {
				description: "Please check your network connection and try again",
			});
			return;
		}

		client.call(numberToCall, {
			"X-PH-Recording-Header": playDisclaimer ? "true" : "false",
			"X-PH-From-Number": fromNumber,
		});
	};

	const hangUp = () => {
		plivoClient.current?.client?.hangup();
		onCallEnd();
	};

	const toggleMute = () => {
		const client = plivoClient.current?.client;
		if (isMuted) client?.unmute();
		else client?.mute();
		setIsMuted(!isMuted);
	};

	const sendDtmf = (digit: string) => {
		plivoClient.current?.client?.sendDtmf(digit);
	};

	const setSpeaker = (deviceId: string) => {
		// biome-ignore lint/suspicious/noExplicitAny: plivo-browser-sdk types incomplete
		(plivoClient.current?.client as any)?.audio?.speakerDevices?.set(deviceId);
	};

	const toggleNoiseReduction = () => {
		const client = plivoClient.current?.client;
		if (!client) return;
		if (isNoiseReductionOn) {
			client.stopNoiseReduction();
		} else {
			client.startNoiseReduction();
		}
		setIsNoiseReductionOn(!isNoiseReductionOn);
	};

	const formatDuration = (seconds: number): string => {
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
	};

	return {
		makeCall,
		hangUp,
		toggleMute,
		sendDtmf,
		setSpeaker,
		toggleNoiseReduction,
		isMuted,
		isCalling,
		isConnected,
		isNoiseReductionOn,
		callStatus,
		callDuration,
		isLoading,
		inputVolume,
		outputVolume,
		formatDuration,
	};
}
